import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ApplicationList } from '../model/application/application-list.model';
import { Observable } from 'rxjs/Observable';
import { ApplicationListParams } from '../model/application-list-params';
import { ApplicationDetail } from '../model/application/application-detail.model';
import { Store } from '@ngrx/store';
import { selectConfig } from '../../config/config.selector';
import { ApplicationsState } from '../applications.state';
import { selectUrlParams } from '../../url-params/url-params.selector';
import { ApplicationUpdatePermissions } from '../model/application/application-update-permissions.model';
import { AccountTransformer } from '../../../core/services/account-transformer.service';
import { AccountGroup } from '../../../core/model/account-group/account-group.model';
import { AbstractRestClient } from '../../../core/services/abstract-rest-client.service';
import { ConfigModel } from '../../config/config.model';
import { UrlParams } from '../../url-params/model/url-params.model';

import 'rxjs/add/operator/filter';
import 'rxjs/add/operator/combineLatest';

/**
 * REST Client to access Applications SelfService Endpoints.
 * Client depends on config and access_token. Both has to be available in the store before using this client.
 *
 * see http://docs.int0csaspsd2selfservice.apiary.io/
 */
@Injectable()
export class AppsRestClient extends AbstractRestClient {

    ssfUrl: string;

    constructor(private http: HttpClient, private store: Store<ApplicationsState>, private transformer: AccountTransformer) {
        super();

        this.store.select(selectConfig)
            .combineLatest(this.store.select(selectUrlParams))
            .filter(([config, urlParams]: ([ConfigModel, UrlParams])) => !!config  &&  !!urlParams)
            .subscribe(([config, urlParams]: ([ConfigModel, UrlParams])) => {
                this.ssfUrl = config ? config.apiUrl.ssf : null;
                this.init(urlParams.access_token, config.webApiKey, urlParams.lang);
            });
    }

    /**
     * Evidované aplikace pro klienta - list.
     * Získání seznamu evidovaných aplikaci.
     *
     * https://int0csaspsd2selfservice.docs.apiary.io/#reference/aisp/pisp/evidovane-aplikace-pro-klienta-list/ziskani-seznamu-evidovanych-aplikaci
     */
    public loadEnabledApplicationList(params: ApplicationListParams): Observable<ApplicationList> {
        this.checkInitialized();

        return this.http.get<ApplicationList>(this.ssfUrl + '/my/apps',
            {
                params: this.pagingHttpParams(params)
                    .append('active', 'true')
                    .append('applicationType', 'notCard'),
                headers: this.headers()
            });
    }

    /**
     * Evidované karty pro klienta - list.
     * Získání seznamu evidovaných aplikací s parametrem applicationType = card.
     */
    public loadEnabledCardList(params: ApplicationListParams): Observable<ApplicationList> {
        this.checkInitialized();

        return this.http.get<ApplicationList>(this.ssfUrl + '/my/apps',
            {
                params: this.pagingHttpParams(params)
                    .append('active', 'false')
                    .append('applicationType', 'card'),
                headers: this.headers()
            });
    }

    /**
     * Detail aplikace pro klienta
     * Detail aplikace v případě že aplikace je u uživatele evidována služba vráti seznam účtu s inforamci zda je učet
     * povolen či nikoli seznam účtu je seskupen dle valstníků účtů. V případě že aplikace není u uživatele evidovana
     * vraci seznam účtů, na které ma uživatel pravo nastavit opravnění pro TPP, s přiznakem že jsou účty zakázány pro
     * aplikaci.Službu je možné zavolat s parametrem service, což vyfiltruje pouze účty, které jsou klientovi dostupné
     *
     * http://docs.int0csaspsd2selfservice.apiary.io/#reference/aisp/pisp/evidovana-aplikace-pro-klienta-detail/detail-aplikace-pro-klienta
     */
    public loadApplicationDetail(appId: string): Observable<ApplicationDetail> {
        this.checkInitialized();

        return this.http.get<ApplicationDetail>(this.ssfUrl + '/my/apps/' + appId, { headers: this.headers() });
    }

    /**
     * Delete application.
     *
     * https://int0csaspsd2selfservice.docs.apiary.io/#reference/aisp/pisp/smazani-aplikace/smazani-aplikace
     */
    public deleteApplication(appId: string): Observable<any> {
        this.checkInitialized();

        return this.http.delete(this.ssfUrl + '/my/apps/' + appId, { headers: this.headers() });
    }

    /**
     * Update application permissions.
     *
     * https://int0csaspsd2selfservice.docs.apiary.io
     *                  /#reference/aisp/pisp/nastaveni-opravneni-smazani-aplikace/nastaveni-opravneni-k-uctum-pro-danou-aplikaci-a-klienta
     *
     * @param {string} appId app id
     * @param {AccountGroup[]} accountsGroups modified accounts groups
     * @return {Observable<ApplicationUpdatePermissions>}
     */
    public updateApplicationPermissions(appId: string, accountsGroups: AccountGroup[]): Observable<ApplicationUpdatePermissions> {
        this.checkInitialized();

        return this.http.post<ApplicationUpdatePermissions>(this.ssfUrl + `/my/apps/${appId}`,
            {
                accountsGroups: this.transformer.transform(accountsGroups)
            },
            { headers: this.headers() });
    }

}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./~/angular-router-loader/src!./~/tslint-loader!./src/app/modules/applications/services/apps-rest-client.service.ts